import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Upload, X, Loader2, Sparkles, Image as ImageIcon, ArrowLeft, FileText, Search, Crop, Link as LinkIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import ImageCropper from '../components/ImageCropper';
import TemplateSelectorModal from '../components/TemplateSelectorModal';
import CategoryAttributesSection from '../components/CategoryAttributesSection';
import { devLog, devError, devWarn } from '../utils/logger';
import {
  CONDITIONS as conditions, UNITS as units,
  USE_CATEGORIES, FINISHES, OBJECT_TYPES
} from './createListingConstants';

export default function CreateListingPage() {
  const { id } = useParams(); // For editing
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  
  // Cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropperIndex, setCropperIndex] = useState(null);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // "Import from my website URL" — suppliers migrating from their existing
  // product pages paste a URL, we scrape it and pre-fill the form.
  const [urlImportOpen, setUrlImportOpen] = useState(false);
  const [urlImportValue, setUrlImportValue] = useState('');
  const [urlImporting, setUrlImporting] = useState(false);

  const applyUrlImport = async () => {
    if (!urlImportValue.trim()) { toast.error('Paste a URL first'); return; }
    setUrlImporting(true);
    try {
      const { data } = await axios.post(`${API}/imports/supplier-site/preview`, { url: urlImportValue.trim() });
      const p = data.preview;
      setFormData((prev) => ({
        ...prev,
        title: p.title || prev.title,
        description: p.description || prev.description,
        category: prev.category || (categories.find((c) => c.slug === p.category)?.slug || ''),
        condition: prev.condition || p.condition || '',
        price: p.price ? String(p.price) : prev.price,
        quantity: p.quantity || prev.quantity,
        unit: p.unit || prev.unit,
        location: p.location || prev.location,
      }));
      if (Array.isArray(p.images) && p.images.length > 0) {
        setImages((prev) => {
          const existing = new Set(prev.map((i) => i.url || i));
          const fresh = p.images
            .filter((u) => !existing.has(u))
            .map((u) => ({ url: u, imported: true }));
          return [...prev, ...fresh];
        });
      }
      toast.success(`Imported — ${p.images?.length || 0} image(s), please review fields below`);
      setUrlImportOpen(false);
      setUrlImportValue('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not scrape that URL');
    } finally { setUrlImporting(false); }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    condition: '',
    price: '',
    quantity: 1,
    unit: 'piece',
    location: '',
    location_radius: 100,
    dimensions: '',
    material: '',
    age: '',
    has_digital_model: false,
    digital_model_url: '',
    generate_ai_model: false,  // Tier 2 AI generation option
    b2b_only: false,  // visible only to other suppliers (trade exchange)
    // Listing type - sale, hire, or both
    listing_type: 'sale',
    hire_price_per_day: '',
    hire_price_per_week: '',
    hire_price_per_month: '',
    hire_min_duration: 1,
    hire_bond: '',
    // Generic attributes
    use_categories: [],
    finish: '',
    recyclability: null,
    reusability: null,
    object_type: '',
    // Category-specific attributes
    specific_attributes: {},
    // Chain-of-custody for Green Star Fitouts / Climate Active submissions
    salvage_origin: {
      building_name: '',
      year_built: '',
      year_deconstructed: '',
      city: '',
      project_reference: '',
      notes: '',
    },
    // ISO 20887 disassembly rating (1=poor, 5=excellent) — feeds MCI
    disassembly_rating: '',
    // Expected remaining service life in years
    expected_remaining_life_years: '',
    // Type-III EPD link (EPD Australasia)
    epd_reference_url: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesRes = await axios.get(`${API}/listings/categories`);
        setCategories(categoriesRes.data.categories);

        // If editing, fetch existing listing
        if (id) {
          const listingRes = await axios.get(`${API}/listings/${id}`);
          const listing = listingRes.data;
          // Merge with the default formData so any fields the API doesn't
          // return (use_categories, specific_attributes, hire_*, b2b_only,
          // listing_type, finish, …) keep their safe defaults — otherwise
          // `formData.use_categories.includes(…)` crashes with "Cannot read
          // properties of undefined" on listings missing those keys (most
          // older / externally-imported listings).
          setFormData((prev) => ({
            ...prev,
            title: listing.title ?? '',
            description: listing.description ?? '',
            category: listing.category ?? '',
            condition: listing.condition ?? '',
            price: listing.price != null ? String(listing.price) : '',
            quantity: listing.quantity ?? 1,
            unit: listing.unit ?? 'piece',
            location: listing.location ?? '',
            location_radius: listing.location_radius ?? 100,
            dimensions: listing.dimensions ?? '',
            material: listing.material ?? '',
            age: listing.age ?? '',
            has_digital_model: !!listing.has_digital_model,
            digital_model_url: listing.digital_model_url ?? '',
            b2b_only: !!listing.b2b_only,
            listing_type: listing.listing_type ?? 'sale',
            hire_price_per_day: listing.hire_price_per_day != null ? String(listing.hire_price_per_day) : '',
            hire_price_per_week: listing.hire_price_per_week != null ? String(listing.hire_price_per_week) : '',
            hire_price_per_month: listing.hire_price_per_month != null ? String(listing.hire_price_per_month) : '',
            hire_min_duration: listing.hire_min_duration ?? 1,
            hire_bond: listing.hire_bond != null ? String(listing.hire_bond) : '',
            use_categories: Array.isArray(listing.use_categories) ? listing.use_categories : [],
            finish: listing.finish ?? '',
            recyclability: listing.recyclability ?? null,
            reusability: listing.reusability ?? null,
            object_type: listing.object_type ?? '',
            specific_attributes: listing.specific_attributes && typeof listing.specific_attributes === 'object'
              ? listing.specific_attributes
              : {},
            salvage_origin: {
              building_name: listing.salvage_origin?.building_name ?? '',
              year_built: listing.salvage_origin?.year_built ?? '',
              year_deconstructed: listing.salvage_origin?.year_deconstructed ?? '',
              city: listing.salvage_origin?.city ?? '',
              project_reference: listing.salvage_origin?.project_reference ?? '',
              notes: listing.salvage_origin?.notes ?? '',
            },
            disassembly_rating: listing.disassembly_rating ?? '',
            expected_remaining_life_years: listing.expected_remaining_life_years ?? '',
            epd_reference_url: listing.epd_reference_url ?? '',
          }));
          setImages(listing.images || []);
        }
        
        // Check for template ID in URL params
        const templateId = searchParams.get('template');
        if (templateId && !id) {
          loadTemplate(templateId);
        }
      } catch (error) {
        devError('Error fetching data:', error);
      }
    };

    fetchData();
  }, [id, searchParams]);

  const loadTemplate = async (templateId) => {
    try {
      const res = await axios.get(`${API}/templates/${templateId}`);
      const template = res.data;
      applyTemplate(template);
      // Record template usage
      axios.post(`${API}/templates/${templateId}/use`).catch(() => {});
    } catch (error) {
      devError('Error loading template:', error);
    }
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      title: template.name,
      description: template.description,
      category: template.category,
      material: template.default_material || '',
      dimensions: template.default_dimensions || '',
      unit: template.suggested_unit || 'piece'
    }));
    setTemplateModalOpen(false);
    toast.success(`Template "${template.name}" applied! Fill in the remaining details.`);
  };

  const fetchTemplates = async (search = '', category = '') => {
    setLoadingTemplates(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category && category !== 'all') params.append('category', category);
      
      const res = await axios.get(`${API}/templates?${params.toString()}`);
      setTemplates(res.data);
    } catch (error) {
      devError('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (templateModalOpen) {
      fetchTemplates(templateSearch, templateCategory);
    }
  }, [templateModalOpen, templateSearch, templateCategory]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Open cropper for the uploaded image
        setImageToCrop(reader.result);
        setCropperIndex(images.length); // New image index
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    // Convert blob to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      if (cropperIndex !== null) {
        if (cropperIndex < images.length) {
          // Replace existing image
          setImages((prev) => {
            const newImages = [...prev];
            newImages[cropperIndex] = reader.result;
            return newImages;
          });
        } else {
          // Add new image
          setImages((prev) => [...prev, reader.result]);
        }
      }
      toast.success('Image cropped successfully');
    };
    reader.readAsDataURL(croppedBlob);
  };

  const handleRecropImage = (index) => {
    setImageToCrop(images[index]);
    setCropperIndex(index);
    setCropperOpen(true);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const analyzeImage = async () => {
    if (images.length === 0) {
      toast.error('Please upload an image first');
      return;
    }

    setAnalyzing(true);
    try {
      // Extract base64 from data URL
      const imageBase64 = images[0].split(',')[1];
      
      const response = await axios.post(
        `${API}/ai/analyze-image`,
        { image_base64: imageBase64 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const analysis = response.data;
      
      setFormData((prev) => ({
        ...prev,
        title: analysis.suggested_title || prev.title,
        description: analysis.description || prev.description,
        category: analysis.suggested_category || prev.category,
        condition: analysis.condition?.toLowerCase() || prev.condition,
        material: analysis.material_type || prev.material,
        dimensions: analysis.estimated_dimensions || prev.dimensions
      }));

      toast.success(`AI analysis complete (${Math.round(analysis.confidence * 100)}% confident)`);
    } catch (error) {
      devError('AI analysis error:', error);
      toast.error('Failed to analyze image');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category || !formData.condition || !formData.price || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        images: images,
        disassembly_rating: formData.disassembly_rating ? parseInt(formData.disassembly_rating, 10) : null,
        expected_remaining_life_years: formData.expected_remaining_life_years ? parseInt(formData.expected_remaining_life_years, 10) : null,
      };

      if (id) {
        await axios.put(`${API}/listings/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Listing updated!');
      } else {
        await axios.post(`${API}/listings`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Listing created!');
      }

      navigate('/dashboard');
    } catch (error) {
      devError('Error saving listing:', error);
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid={id ? 'edit-listing-page' : 'create-listing-page'}>
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to dashboard
        </button>

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">
                {id ? 'Edit' : 'Create'} Listing
              </p>
              <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground">
                {id ? 'Update Your Listing' : 'Add New Material'}
              </h1>
            </div>
            {!id && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setUrlImportOpen(true)}
                  className="border-accent/40 text-accent hover:bg-accent/10"
                  data-testid="import-url-btn"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Import from URL
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTemplateModalOpen(true)}
                  className="border-accent/40 text-accent hover:bg-accent/10"
                  data-testid="use-template-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Use Template
                </Button>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid={id ? 'edit-listing-form' : 'create-listing-form'}>
          {/* Image Upload Section */}
          <div className="p-6 rounded-lg bg-card/50 border border-border/20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-playfair text-xl font-semibold">Images</h2>
              {images.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={analyzeImage}
                  disabled={analyzing}
                  className="border-primary/40 text-primary hover:bg-primary/10"
                  data-testid="analyze-image-btn"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  AI Analyze
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {images.map((img, index) => (
                <div key={`upload-${img.substring(0, 20)}-${index}`} className="relative aspect-square rounded-lg overflow-hidden group">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRecropImage(index)}
                    className="absolute top-2 left-2 px-2 py-1 rounded bg-black/50 flex items-center gap-1 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    title="Re-crop image"
                  >
                    <Crop className="w-3 h-3" />
                    Crop
                  </button>
                  {index === 0 && (
                    <span className="absolute bottom-2 left-2 px-2 py-1 text-xs bg-accent text-accent-foreground rounded">
                      Primary
                    </span>
                  )}
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded-lg border-2 border-dashed border-border/40 hover:border-accent/40 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-accent"
                data-testid="upload-image-btn"
              >
                <Upload className="w-6 h-6" />
                <span className="text-xs">Add Image</span>
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              data-testid="image-input"
            />

            <p className="mt-4 text-xs text-muted-foreground">
              Upload images of your material. First image will be the primary image. Use AI Analyze to auto-fill details.
            </p>
          </div>

          {/* Basic Details */}
          <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-6">
            <h2 className="font-playfair text-xl font-semibold">Basic Details</h2>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                placeholder="e.g., Victorian Cast Iron Radiator"
                required
                data-testid="title-input"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full h-32 p-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50 resize-none"
                placeholder="Describe your material in detail..."
                required
                data-testid="description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Category *</label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="h-12 bg-secondary/30 border-border/40" data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Condition *</label>
                <Select 
                  value={formData.condition} 
                  onValueChange={(v) => setFormData({ ...formData, condition: v })}
                >
                  <SelectTrigger className="h-12 bg-secondary/30 border-border/40" data-testid="condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map((cond) => (
                      <SelectItem key={cond.id} value={cond.id}>
                        {cond.name} - {cond.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Pricing & Quantity */}
          <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-6">
            <h2 className="font-playfair text-xl font-semibold">Pricing & Quantity</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Price (USD) *</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                  data-testid="price-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Quantity</label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground"
                  min="1"
                  data-testid="quantity-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Unit</label>
                <Select 
                  value={formData.unit} 
                  onValueChange={(v) => setFormData({ ...formData, unit: v })}
                >
                  <SelectTrigger className="h-12 bg-secondary/30 border-border/40" data-testid="unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-6">
            <h2 className="font-playfair text-xl font-semibold">Location</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="City, Country"
                  required
                  data-testid="location-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Delivery Radius (km)</label>
                <Select 
                  value={formData.location_radius.toString()} 
                  onValueChange={(v) => setFormData({ ...formData, location_radius: parseInt(v) })}
                >
                  <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">Within 20 km</SelectItem>
                    <SelectItem value="50">Within 50 km</SelectItem>
                    <SelectItem value="100">Within 100 km</SelectItem>
                    <SelectItem value="500">Within 500 km</SelectItem>
                    <SelectItem value="1000">Nationwide</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-6">
            <h2 className="font-playfair text-xl font-semibold">Specifications (Optional)</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Material</label>
                <input
                  type="text"
                  value={formData.material}
                  onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="e.g., Oak, Cast Iron"
                  data-testid="material-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Dimensions</label>
                <input
                  type="text"
                  value={formData.dimensions}
                  onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="e.g., 200x100x50 cm"
                  data-testid="dimensions-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Age</label>
                <input
                  type="text"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="e.g., Victorian era, ~1890"
                  data-testid="age-input"
                />
              </div>
            </div>
          </div>

          {/* Salvage origin / chain of custody — for Green Star Fitouts,
              Climate Active & NABERS submissions. All optional but
              strongly recommended — they make every listing usable as
              verifiable compliance evidence. */}
          <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-4" data-testid="salvage-origin-section">
            <div>
              <h2 className="font-playfair text-xl font-semibold">Salvage origin <span className="text-xs font-sans text-muted-foreground ml-2">(optional — chain of custody)</span></h2>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                Where this item came from. Populating these fields turns every listing into Green Star Fitouts <strong>Design for Reuse</strong> + Climate Active Upfront Carbon evidence — designers filter for this.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Originally installed at</label>
                <input
                  type="text"
                  value={formData.salvage_origin.building_name}
                  onChange={(e) => setFormData({ ...formData, salvage_origin: { ...formData.salvage_origin, building_name: e.target.value } })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="e.g., Royal Hotel, Gippsland"
                  data-testid="salvage-building-input"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">City / location</label>
                <input
                  type="text"
                  value={formData.salvage_origin.city}
                  onChange={(e) => setFormData({ ...formData, salvage_origin: { ...formData.salvage_origin, city: e.target.value } })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="e.g., Melbourne VIC"
                  data-testid="salvage-city-input"
                  maxLength={100}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Year built</label>
                <input
                  type="text"
                  value={formData.salvage_origin.year_built}
                  onChange={(e) => setFormData({ ...formData, salvage_origin: { ...formData.salvage_origin, year_built: e.target.value } })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="e.g., 1895"
                  data-testid="salvage-yearbuilt-input"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Year deconstructed</label>
                <input
                  type="text"
                  value={formData.salvage_origin.year_deconstructed}
                  onChange={(e) => setFormData({ ...formData, salvage_origin: { ...formData.salvage_origin, year_deconstructed: e.target.value } })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="e.g., 2024"
                  data-testid="salvage-yeardecon-input"
                  maxLength={10}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-muted-foreground mb-2 block">Project / salvage reference <span className="text-muted-foreground/50">(optional)</span></label>
                <input
                  type="text"
                  value={formData.salvage_origin.project_reference}
                  onChange={(e) => setFormData({ ...formData, salvage_origin: { ...formData.salvage_origin, project_reference: e.target.value } })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="Internal reference number, deconstruction permit, heritage ID, etc."
                  data-testid="salvage-projectref-input"
                  maxLength={200}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-muted-foreground mb-2 block">Notes <span className="text-muted-foreground/50">(condition, provenance, heritage story — shown on the public passport)</span></label>
                <textarea
                  value={formData.salvage_origin.notes}
                  onChange={(e) => setFormData({ ...formData, salvage_origin: { ...formData.salvage_origin, notes: e.target.value } })}
                  rows={2}
                  className="w-full px-4 py-3 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="Removed carefully from the main staircase during 2024 heritage restoration."
                  data-testid="salvage-notes-input"
                  maxLength={500}
                />
              </div>
            </div>

            <div className="pt-4 mt-2 border-t border-border/20">
              <p className="text-xs text-muted-foreground mb-3">
                <strong className="text-foreground/80">Circularity scoring</strong> — feeds the Material Circularity Indicator (MCI), per ISO 59020:2024 + EMF method.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Disassembly rating <span className="text-muted-foreground/50">(ISO 20887)</span></label>
                  <select
                    value={formData.disassembly_rating}
                    onChange={(e) => setFormData({ ...formData, disassembly_rating: e.target.value })}
                    className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground"
                    data-testid="disassembly-rating-input"
                  >
                    <option value="">—</option>
                    <option value="1">1 · Welded / glued, not separable</option>
                    <option value="2">2 · Mechanically fixed, hard to remove</option>
                    <option value="3">3 · Standard fixings, removable</option>
                    <option value="4">4 · Designed for disassembly</option>
                    <option value="5">5 · Modular / clip-fit, fully reusable</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Remaining service life</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="200"
                      value={formData.expected_remaining_life_years}
                      onChange={(e) => setFormData({ ...formData, expected_remaining_life_years: e.target.value })}
                      className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                      placeholder="e.g., 50"
                      data-testid="remaining-life-input"
                    />
                    <span className="text-sm text-muted-foreground">years</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">EPD reference URL <span className="text-muted-foreground/50">(if available)</span></label>
                  <input
                    type="url"
                    value={formData.epd_reference_url}
                    onChange={(e) => setFormData({ ...formData, epd_reference_url: e.target.value })}
                    className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                    placeholder="https://epd-australasia.com/epd/..."
                    data-testid="epd-url-input"
                    maxLength={500}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Generic Attributes */}
          <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-6">
            <h2 className="font-playfair text-xl font-semibold">Product Attributes</h2>

            {/* Use Categories - Multi-select */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">
                Use Categories (select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {USE_CATEGORIES.map((cat) => (
                  <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.use_categories.includes(cat.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            use_categories: [...formData.use_categories, cat.id]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            use_categories: formData.use_categories.filter(c => c !== cat.id)
                          });
                        }
                      }}
                      className="w-4 h-4 rounded border-border/40 text-accent focus:ring-accent"
                    />
                    <span className="text-sm">{cat.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Finish */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Finish</label>
                <Select 
                  value={formData.finish} 
                  onValueChange={(v) => setFormData({ ...formData, finish: v })}
                >
                  <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                    <SelectValue placeholder="Select finish" />
                  </SelectTrigger>
                  <SelectContent>
                    {FINISHES.map((finish) => (
                      <SelectItem key={finish.id} value={finish.id}>{finish.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Object Type */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Object Type</label>
                <Select 
                  value={formData.object_type} 
                  onValueChange={(v) => setFormData({ ...formData, object_type: v })}
                >
                  <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                    <SelectValue placeholder="Select object type" />
                  </SelectTrigger>
                  <SelectContent>
                    {OBJECT_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recyclability & Reusability Scales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Recyclability (1-5 scale)
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, recyclability: value })}
                      className={`flex-1 h-12 rounded-lg border-2 transition-all ${
                        formData.recyclability === value
                          ? 'border-accent bg-accent/10 text-accent font-semibold'
                          : 'border-border/40 hover:border-accent/40'
                      }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  1 = Low recyclability, 5 = Highly recyclable
                </p>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Reusability (1-5 scale)
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, reusability: value })}
                      className={`flex-1 h-12 rounded-lg border-2 transition-all ${
                        formData.reusability === value
                          ? 'border-accent bg-accent/10 text-accent font-semibold'
                          : 'border-border/40 hover:border-accent/40'
                      }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  1 = Single use, 5 = Highly reusable
                </p>
              </div>
            </div>
          </div>

          {/* Category-Specific Attributes */}
          <CategoryAttributesSection
            formData={formData}
            setFormData={setFormData}
            categories={categories}
          />

          {/* Digital Model */}
          <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-6">
            <h2 className="font-playfair text-xl font-semibold">3D Models (BIM/Revit)</h2>
            
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
              <p className="text-sm text-foreground mb-2">
                <strong>✨ Automatic 3D Model Generation</strong>
              </p>
              <p className="text-sm text-muted-foreground">
                For <strong>beams, bricks, doors, windows, and flooring</strong> with valid dimensions, 
                we'll automatically generate a basic 3D model (IFC + GLB) for free!
              </p>
            </div>

            {/* Tier 2: AI Model Generation Option */}
            {!['beams', 'bricks', 'doors', 'windows', 'flooring'].includes(formData.category) && images.length > 0 && (
              <div className="p-4 rounded-lg bg-accent/10 border border-accent/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.generate_ai_model}
                    onChange={(e) => setFormData({ ...formData, generate_ai_model: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-border/40 bg-secondary/30 text-accent"
                    data-testid="generate-ai-model-checkbox"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground mb-1">
                      🤖 Generate AI 3D Model (+$0.50 AUD)
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Create a high-quality AI-generated 3D model from your images. 
                      Perfect for complex shapes and ornate items. Takes 30-60 seconds.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Manual Model URL (existing functionality) */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={formData.has_digital_model}
                  onChange={(e) => setFormData({ ...formData, has_digital_model: e.target.checked })}
                  className="w-5 h-5 rounded border-border/40 bg-secondary/30 text-accent"
                  data-testid="has-model-checkbox"
                />
                <span className="text-sm text-muted-foreground">I have an existing 3D model URL</span>
              </label>

              {formData.has_digital_model && (
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Model URL (Revit/IFC)</label>
                  <input
                    type="url"
                    value={formData.digital_model_url}
                    onChange={(e) => setFormData({ ...formData, digital_model_url: e.target.value })}
                    className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                    placeholder="https://..."
                    data-testid="model-url-input"
                  />
                </div>
              )}
            </div>
          </div>

          {/* B2B-only / Trade Exchange toggle */}
          <div className="p-4 rounded-lg border border-amber-500/20 bg-amber-500/5" data-testid="b2b-only-toggle-section">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.b2b_only}
                onChange={(e) => setFormData({ ...formData, b2b_only: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-border/40 bg-secondary/30 text-amber-400"
                data-testid="b2b-only-checkbox"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">
                  Supplier-only (Trade Exchange)
                </p>
                <p className="text-xs text-muted-foreground">
                  Hide this listing from public buyers. Only other verified suppliers will see it
                  in the <strong>Trade Exchange</strong>. Perfect for surplus stock, giveaways,
                  or specialised items you'd rather pass on to a fellow yard.
                </p>
              </div>
            </label>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex-1 h-12 border-border/40"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 btn-gold"
              data-testid="submit-listing-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : id ? (
                'Update Listing'
              ) : (
                'Create Listing'
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Template Selection Modal */}
      <TemplateSelectorModal
        open={templateModalOpen}
        onOpenChange={setTemplateModalOpen}
        templates={templates}
        categories={categories}
        loadingTemplates={loadingTemplates}
        templateSearch={templateSearch}
        setTemplateSearch={setTemplateSearch}
        templateCategory={templateCategory}
        setTemplateCategory={setTemplateCategory}
        applyTemplate={applyTemplate}
      />

      {/* Image Cropper Dialog */}
      <ImageCropper
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
      />

      {/* Import from URL Dialog — paste your existing website's product page */}
      <Dialog open={urlImportOpen} onOpenChange={setUrlImportOpen}>
        <DialogContent className="max-w-lg" data-testid="url-import-dialog">
          <DialogHeader>
            <DialogTitle>Import from a URL</DialogTitle>
            <DialogDescription>
              Paste a product page URL from your existing website or supplier portal —
              we'll grab the title, description, price and images to pre-fill the form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <input
              type="url"
              value={urlImportValue}
              onChange={(e) => setUrlImportValue(e.target.value)}
              placeholder="https://your-yard.com.au/reclaimed-bricks-1880s"
              className="w-full h-10 rounded-md border border-border/30 bg-background/40 px-3 text-sm"
              data-testid="url-import-input"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Imported images appear below ready to crop &amp; reorder. Double-check category,
              price and condition before saving — we do our best but external pages vary.
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="outline" onClick={() => setUrlImportOpen(false)}>Cancel</Button>
            <Button onClick={applyUrlImport} disabled={urlImporting} className="btn-gold" data-testid="url-import-submit">
              {urlImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Scrape &amp; import
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

